import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Reducer } from 'redux';

import { QuestionnaireBlueprint, SerializedNodeIdToAnswerPathMap } from '@zinnia/form-engine-sdk';

import { Step } from '../../Models/Step';

export type StepState = {
  currentStep: Step | null;
  nextStep: Step | null;
  isLoading: boolean;
  loadingStepId: string | null;
  hasErrors: boolean;
  nodeIdToAnswerPathMap: SerializedNodeIdToAnswerPathMap | null;
  blueprint: QuestionnaireBlueprint | null;
};

const defaultState: StepState = {
  currentStep: null,
  nextStep: null,
  isLoading: false,
  loadingStepId: null,
  hasErrors: false,
  nodeIdToAnswerPathMap: null,
  blueprint: null,
};

type StepPayload = {
  step: Step | null;
};

type IsLoadingPayload = {
  isLoading: boolean;
  stepId: string | null;
};

type HasErrorsPayload = {
  hasErrors: boolean;
};

type NodeIdToAnswerPathMapPayload = {
  nodeIdToAnswerPathMap: SerializedNodeIdToAnswerPathMap | null;
};

type StepActions = {
  setCurrentStep: (payload: StepPayload) => PayloadAction<StepPayload>;
  setNextStep: (payload: StepPayload) => PayloadAction<StepPayload>;
  setIsLoading: (payload: IsLoadingPayload) => PayloadAction<IsLoadingPayload>;
  reset: () => PayloadAction<undefined>;
  setHasErrors: (payload: HasErrorsPayload) => PayloadAction<HasErrorsPayload>;
  setNodeIdToAnswerPathMap: (payload: NodeIdToAnswerPathMapPayload) => PayloadAction<NodeIdToAnswerPathMapPayload>;
  setBlueprint: (payload: QuestionnaireBlueprint) => PayloadAction<QuestionnaireBlueprint>;
};

// `QuestionnaireBlueprint` is too deep a recursive type (form-engine-sdk blueprint/condition types)
// for TS to compute `Draft<StepState>` against (TS2589). Build the slice with the state generic left
// untyped, then cast `.reducer`/`.actions` back to the real shape below for consumers.
const untypedStepSlice = createSlice({
  name: 'consumerFlow/step',
  initialState: defaultState as any,
  reducers: {
    setCurrentStep(state: StepState, { payload }: PayloadAction<StepPayload>) {
      state.currentStep = payload.step;
    },
    setNextStep(state: StepState, { payload }: PayloadAction<StepPayload>) {
      state.nextStep = payload.step;
    },
    setIsLoading(state: StepState, { payload }: PayloadAction<IsLoadingPayload>) {
      state.isLoading = payload.isLoading;
      state.loadingStepId = payload.stepId;
    },
    reset(state: StepState) {
      state.isLoading = false;
      state.loadingStepId = null;
      state.currentStep = null;
      state.nextStep = null;
      state.nodeIdToAnswerPathMap = null;
    },
    setHasErrors(state: StepState, { payload }: PayloadAction<HasErrorsPayload>) {
      state.hasErrors = payload.hasErrors;
    },
    setNodeIdToAnswerPathMap(state: StepState, { payload }: PayloadAction<NodeIdToAnswerPathMapPayload>) {
      state.nodeIdToAnswerPathMap = payload.nodeIdToAnswerPathMap;
    },
    setBlueprint(state: StepState, { payload }: PayloadAction<QuestionnaireBlueprint>) {
      state.blueprint = payload;
    },
  },
});

export const stepSlice = {
  name: untypedStepSlice.name,
  reducer: untypedStepSlice.reducer as Reducer<StepState>,
  actions: untypedStepSlice.actions as unknown as StepActions,
};
